import { notifyProgress } from '@billy/design-system'
import { pdf } from '@react-pdf/renderer'
import download from 'downloadjs'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PeriodValue } from '@features/reports/types/inputTypes'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { ReportsPdfDocument } from '@views/reports/elements/ReportsPdfDocument'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { downloadStatusToTKeys } from '../../../utils/export/downloadStatusToTKeys'
import { sanitizeFileName } from '../../../utils/export/sanitizeFileName'
import {
  ReportsPdfDocumentCommonProps,
  ReportsPdfDocumentVariant,
} from '../elements/ReportsPdfDocument/types/reportsPdfDocument'

export type ReportsPdfDocumentProps = ReportsPdfDocumentVariant & ReportsPdfDocumentCommonProps

type UseReportPdfExportProps = ReportsPdfDocumentVariant & {
  allExpandableIds?: string[]
  fileName: string
  title: string
  periods?: PeriodValue[]
}

const buildPdfDocument = (props: ReportsPdfDocumentProps) => {
  return <ReportsPdfDocument {...props} />
}

export const useReportPdfExport = (props: UseReportPdfExportProps) => {
  const { fileName, allExpandableIds, ...reportContent } = props

  const [isLoading, setIsLoading] = useState(false)

  const { t } = useTranslation()
  const { organization } = useUserOrganization()

  const organizationName = organization?.name || ''

  const expandedRowsForPdf = useMemo(() => {
    const newExpanded: Record<string, boolean> = {}
    if (allExpandableIds) {
      for (const id of allExpandableIds) {
        newExpanded[id] = true
      }
    }
    return newExpanded
  }, [allExpandableIds])

  const document = useMemo(() => {
    const pdfProps: ReportsPdfDocumentProps = {
      ...reportContent,
      organizationName,
      expandedRows: expandedRowsForPdf,
    }

    return buildPdfDocument(pdfProps)
  }, [reportContent, organizationName, expandedRowsForPdf])

  const exportAsPdf = useCallback(async () => {
    try {
      setIsLoading(true)
      const blob = await pdf(document).toBlob()
      const finalName = `${fileName}-${sanitizeFileName(organizationName)}.pdf`
      download(blob, finalName)

      const successTKeys = downloadStatusToTKeys.success
      notifyProgress({
        id: NotificationKeys.ReportsExportPdf,
        title: t(successTKeys.title),
        description: t(successTKeys.description),
        variant: 'success',
        progress: 100,
        closable: true,
      })
      setIsLoading(false)
    } catch {
      const errorTKeys = downloadStatusToTKeys.error
      setIsLoading(false)
      notifyProgress({
        id: NotificationKeys.ReportsExportPdf,
        title: t(errorTKeys.title),
        description: t(errorTKeys.description),
        variant: 'error',
        progress: 100,
        closable: true,
      })
    }
  }, [document, fileName, organizationName, t])

  return { exportAsPdf, isLoading }
}
